import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import * as api from '../services/api';

interface AuditResult {
  execution_id: string;
  agent_id: string;
  agent_hostname: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  duration: number | null;
  summary: Record<string, any> | null;
  agent_version: string;
  created_at: string;
}

interface AuditSummary {
  total_scans: number;
  completed: number;
  failed: number;
  avg_score: number | null;
  last_scan_at: string | null;
}

const PAGE_SIZE = 20;

const statusBadge = (status: string) => {
  const s = status?.toUpperCase() || 'UNKNOWN';
  switch (s) {
    case 'COMPLETED':
      return 'bg-green-500/20 text-green-400 border border-green-500/30';
    case 'FAILED':
      return 'bg-red-500/20 text-red-400 border border-red-500/30';
    case 'RUNNING':
      return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
    case 'PENDING':
      return 'bg-white/10 text-white/50 border border-white/10';
    default:
      return 'bg-white/10 text-white/50 border border-white/10';
  }
};

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '--';
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return `${mins}m ${rem}s`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getScoreFromResult = (result: any): string => {
  if (result.overall_score != null) return `${result.overall_score}%`;
  if (result.summary && typeof result.summary === 'object' && result.summary.score != null) {
    return `${result.summary.score}%`;
  }
  return '--';
};

const getGradeFromResult = (result: any): string => {
  if (result.grade) return result.grade;
  if (result.summary && typeof result.summary === 'object' && result.summary.grade) {
    return result.summary.grade;
  }
  return '--';
};

const AuditPage: React.FC = () => {
  const [results, setResults] = useState<AuditResult[]>([]);
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await api.exportAuditResultsCsv();
    } catch (err: any) {
      setError(err.message || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const offset = page * PAGE_SIZE;
      // Fetch one extra row to detect if there's a next page
      const [resultsData, summaryData] = await Promise.all([
        api.getAuditResults({ limit: PAGE_SIZE + 1, offset }),
        api.getAuditSummary(),
      ]);

      const rows = Array.isArray(resultsData) ? resultsData : [];
      setHasMore(rows.length > PAGE_SIZE);
      setResults(rows.slice(0, PAGE_SIZE));
      setSummary(summaryData);
    } catch (err: any) {
      setError(err.message || 'Failed to load audit data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const filteredResults = statusFilter
    ? results.filter((r) => r.status?.toUpperCase() === statusFilter)
    : results;

  // Summary cards
  const SummaryCards = () => {
    if (!summary) return null;
    const cards = [
      { label: 'Total Scans', value: summary.total_scans, color: 'text-anchor-blue' },
      { label: 'Completed', value: summary.completed, color: 'text-green-400' },
      { label: 'Failed', value: summary.failed, color: 'text-red-400' },
      {
        label: 'Avg Score',
        value: summary.avg_score != null ? `${Number(summary.avg_score).toFixed(1)}%` : '--',
        color: 'text-yellow-400',
      },
    ];

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div
            key={c.label}
            className="bg-anchor-card border border-white/[0.06] rounded-xl p-5"
          >
            <div className="text-xs text-anchor-muted uppercase tracking-wider mb-2">
              {c.label}
            </div>
            <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-medium text-white">Audit Log</h2>
          <p className="text-anchor-muted mt-1">Security audit trail and compliance reporting</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-anchor-blue mx-auto"></div>
            <p className="mt-4 text-anchor-muted text-sm">Loading audit data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-medium text-white">Audit Log</h2>
          <p className="text-anchor-muted mt-1">Security audit trail and compliance reporting</p>
        </div>
        <div className="bg-anchor-card border border-red-500/20 rounded-xl p-12 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="text-red-400 text-sm mb-4">{error}</p>
          <button
            onClick={() => fetchData()}
            className="px-4 py-2 bg-white/[0.06] border border-white/[0.1] rounded-lg text-sm text-white hover:bg-white/[0.1] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-medium text-white">Audit Log</h2>
          <p className="text-anchor-muted mt-1">Security audit trail and compliance reporting</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCsv}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-anchor-light hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            <ArrowDownTrayIcon className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-anchor-light hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <SummaryCards />

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-anchor-card border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-anchor-blue/50"
        >
          <option value="">All Statuses</option>
          <option value="COMPLETED">Completed</option>
          <option value="FAILED">Failed</option>
          <option value="RUNNING">Running</option>
          <option value="PENDING">Pending</option>
        </select>
        {statusFilter && (
          <button
            onClick={() => setStatusFilter('')}
            className="text-xs text-anchor-muted hover:text-white transition-colors"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Results table */}
      <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/[0.06]">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">
                  Grade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {filteredResults.map((r) => (
                <tr
                  key={r.execution_id}
                  className="hover:bg-white/[0.02] transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                    {formatDate(r.started_at || r.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {r.agent_hostname || r.agent_id?.slice(0, 8)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusBadge(r.status)}`}
                    >
                      {r.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                    {getScoreFromResult(r)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                    {getGradeFromResult(r)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/50">
                    {formatDuration(r.duration)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {filteredResults.length === 0 && (
          <div className="text-center py-16">
            <ClipboardDocumentCheckIcon className="h-10 w-10 text-anchor-muted mx-auto mb-3" />
            <p className="text-sm text-anchor-muted">
              {statusFilter
                ? `No ${statusFilter.toLowerCase()} audit results found`
                : 'No audit results found'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {(page > 0 || hasMore) && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-anchor-muted">Page {page + 1}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-anchor-light hover:bg-white/[0.08] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-anchor-light hover:bg-white/[0.08] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditPage;
